<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactEditableField from './ContactEditableField.vue';
import ContactOtherDetails from './ContactOtherDetails.vue';
import SocialIcons from './SocialIcons.vue';
import Avatar from 'next/avatar/Avatar.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    ContactEditableField,
    ContactOtherDetails,
    SocialIcons,
    EditContact,
    Avatar,
    ComposeConversation,
    ContactMergeModal,
    VoiceCallButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['panelClose'],
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showMergeModal: false,
      showDeleteModal: false,
      showAvatarPreview: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.contact.additional_attributes || {};
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleAvatarPreview() {
      if (!this.contact.thumbnail) return;
      this.showAvatarPreview = !this.showAvatarPreview;
    },
    closeAvatarPreview() {
      this.showAvatarPreview = false;
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openComposeConversationModal(toggleFn) {
      toggleFn();
      // Flag to prevent triggering drag n drop,
      // When compose modal is active
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
    },
    closeComposeConversationModal() {
      // Flag to enable drag n drop,
      // When compose modal is closed
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
    async updateContactField(field, value) {
      try {
        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          [field]: value,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error?.data || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex items-center justify-between w-full gap-3">
        <div v-if="showAvatar" class="flex flex-col items-center flex-shrink-0">
          <button
            type="button"
            class="cursor-pointer"
            :class="{ 'cursor-default': !contact.thumbnail }"
            @click="toggleAvatarPreview"
          >
            <Avatar
              :src="contact.thumbnail"
              :name="contact.name"
              :status="contact.availability_status"
              :size="64"
              hide-offline-status
              rounded-full
            />
          </button>
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
        <div class="flex items-center justify-end flex-1 min-w-0 gap-2">
          <ComposeConversation
            :contact-id="String(contact.id)"
            is-modal
            @close="closeComposeConversationModal"
          >
            <template #trigger="{ toggle }">
              <NextButton
                v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
                icon="i-ph-chat-circle-dots"
                slate
                faded
                sm
                @click="openComposeConversationModal(toggle)"
              />
            </template>
          </ComposeConversation>
          <VoiceCallButton
            :phone="contact.phone_number"
            :contact-id="contact.id"
            icon="i-ri-phone-fill"
            size="sm"
            :tooltip-label="$t('CONTACT_PANEL.CALL')"
            slate
            faded
          />
          <NextButton
            v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
            icon="i-ph-pencil-simple"
            slate
            faded
            sm
            @click="toggleEditModal"
          />
          <NextButton
            v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
            icon="i-ph-arrows-merge"
            slate
            faded
            sm
            :disabled="uiFlags.isMerging"
            @click="openMergeModal"
          />
          <NextButton
            v-if="isAdmin"
            v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
            icon="i-ph-trash"
            slate
            faded
            sm
            ruby
            :disabled="uiFlags.isDeleting"
            @click="toggleDeleteModal"
          />
        </div>
      </div>

      <div
        class="flex flex-col items-start min-w-0 w-full overflow-visible rounded-xl bg-n-solid-2 divide-y divide-n-weak/60"
      >
        <div v-if="showAvatar" class="flex items-center w-full min-w-0">
          <ContactEditableField
            class="flex-1 min-w-0"
            icon="i-lucide-user-round"
            label="Nome"
            :value="contact.name"
            required
            @update="value => updateContactField('name', value)"
          />
          <div class="flex flex-row items-center flex-shrink-0 gap-2 pr-3">
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <div class="flex flex-col items-start w-full divide-y divide-n-weak/60">
          <ContactEditableField
            :value="contact.phone_number"
            :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
            icon="i-lucide-phone"
            input-type="tel"
            label="Telefone"
            show-copy
            @update="value => updateContactField('phone_number', value)"
          />
          <ContactOtherDetails :contact="contact" />
        </div>
      </div>
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <ContactMergeModal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="closeMergeModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
    <woot-modal
      v-if="showAvatarPreview"
      v-model:show="showAvatarPreview"
      :on-close="closeAvatarPreview"
    >
      <div class="flex items-center justify-center p-8">
        <img
          :src="contact.thumbnail"
          :alt="contact.name"
          class="object-contain rounded-lg max-h-[70vh] max-w-full"
        />
      </div>
    </woot-modal>
  </div>
</template>
